import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, RefreshControl } from 'react-native';
import { Text, Card, Button, Divider, ActivityIndicator, TextInput, RadioButton, Portal, Modal } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useRoute, useNavigation } from '@react-navigation/native';
import api from '../../services/api';

const DetalleCuentaScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { cuentaId } = route.params;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cuenta, setCuenta] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [detallesVenta, setDetallesVenta] = useState([]);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagoLoading, setPagoLoading] = useState(false);
  const [montoPago, setMontoPago] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [notasPago, setNotasPago] = useState('');

  const formatCurrency = (v) => `L. ${parseFloat(v || 0).toFixed(2)}`;

  const cargarDetalle = useCallback(async () => {
    try {
      const response = await api.get(`/creditos/cuentas/${cuentaId}`);
      setCuenta(response.cuenta);
      setCuotas(response.cuotas || []);
      setPagos(response.pagos || []);
      setDetallesVenta(response.detalles_venta || []);
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudo cargar el detalle');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [cuentaId]);

  useEffect(() => { cargarDetalle(); }, [cargarDetalle]);

  const handleRegistrarPago = async () => {
    const monto = parseFloat(montoPago);
    if (!monto || monto <= 0) {
      Alert.alert('Error', 'Ingrese un monto válido');
      return;
    }
    if (monto > parseFloat(cuenta.saldo_pendiente)) {
      Alert.alert('Error', `El monto no puede exceder el saldo pendiente de ${formatCurrency(cuenta.saldo_pendiente)}`);
      return;
    }

    try {
      setPagoLoading(true);
      const response = await api.post('/creditos/pagos', {
        cuenta_id: cuentaId,
        monto,
        metodo_pago: metodoPago,
        notas: notasPago || null,
      });

      // Actualización optimista para que la barra y el estado se reflejen al instante,
      // sin esperar el refetch.
      setCuenta(prev => prev ? {
        ...prev,
        monto_pagado: parseFloat(prev.monto_pagado || 0) + parseFloat(response.monto_pagado || monto),
        saldo_pendiente: response.saldo_restante,
        estado: response.estado || prev.estado,
      } : prev);

      setShowPagoModal(false);
      setMontoPago('');
      setNotasPago('');
      Alert.alert('Pago Registrado', `Abono de ${formatCurrency(monto)} registrado.\nSaldo restante: ${formatCurrency(response.saldo_restante)}`);
      cargarDetalle();
    } catch (error) {
      console.error('Error al registrar pago:', error);
      Alert.alert('Error', error.message || 'No se pudo registrar el pago');
    } finally {
      setPagoLoading(false);
    }
  };

  const getEstadoStyle = (estado, diasRestantes) => {
    if (estado === 'pagada') return { bg: '#28A745', textColor: '#fff', label: 'Pagada', icon: 'checkmark-circle' };
    if (diasRestantes < 0)   return { bg: '#DC3545', textColor: '#fff', label: `Vencida ${Math.abs(diasRestantes)}d`, icon: 'alert-circle' };
    if (diasRestantes <= 3)  return { bg: '#FFC107', textColor: '#000', label: `Por vencer ${diasRestantes}d`, icon: 'time' };
    if (estado === 'parcial') return { bg: '#17A2B8', textColor: '#fff', label: 'Pago parcial', icon: 'reload-circle' };
    return { bg: '#6C757D', textColor: '#fff', label: `Pendiente ${diasRestantes}d`, icon: 'ellipse' };
  };

  if (loading) {
    return <View style={styles.loader}><ActivityIndicator size="large" color="#0066cc" /></View>;
  }

  if (!cuenta) {
    return <View style={styles.loader}><Text>Cuenta no encontrada</Text></View>;
  }

  const est = getEstadoStyle(cuenta.estado, cuenta.dias_restantes);
  const porcentajePagado = parseFloat(cuenta.monto_total) > 0
    ? (parseFloat(cuenta.monto_pagado) / parseFloat(cuenta.monto_total)) * 100 : 0;

  return (
    <ScrollView style={styles.container} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); cargarDetalle(); }} />}>
      {/* Info del cliente */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.headerRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.clienteNombre}>{cuenta.cliente_nombre}</Text>
              {cuenta.nombre_negocio ? <Text style={styles.negocioNombre}>{cuenta.nombre_negocio}</Text> : null}
              <Text style={styles.subtext}>{cuenta.cliente_telefono}</Text>
              {cuenta.vendedor_nombre ? <Text style={styles.subtext}>Vendedor: {cuenta.vendedor_nombre}</Text> : null}
            </View>
            <View style={[styles.estadoChip, { backgroundColor: est.bg }]}>
              <Ionicons name={est.icon} size={14} color={est.textColor} />
              <Text style={[styles.estadoChipText, { color: est.textColor }]}>{est.label}</Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Resumen financiero */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Resumen de Cuenta</Text>
          <View style={styles.finRow}>
            <Text style={styles.finLabel}>Total Factura</Text>
            <Text style={styles.finValue}>{formatCurrency(cuenta.monto_total)}</Text>
          </View>
          <View style={styles.finRow}>
            <Text style={styles.finLabel}>Total Pagado</Text>
            <Text style={[styles.finValue, { color: '#28a745' }]}>{formatCurrency(cuenta.monto_pagado)}</Text>
          </View>
          <Divider style={{ marginVertical: 8 }} />
          <View style={styles.finRow}>
            <Text style={[styles.finLabel, { fontWeight: '800', fontSize: 16 }]}>Saldo Pendiente</Text>
            <Text style={[styles.finValue, { color: '#dc3545', fontSize: 20 }]}>{formatCurrency(cuenta.saldo_pendiente)}</Text>
          </View>

          {/* Barra de progreso */}
          <View style={styles.progressContainer}>
            <View style={styles.progressBar}>
              <View style={[styles.progressFill, { width: `${porcentajePagado}%` }]} />
            </View>
            <Text style={styles.progressText}>{porcentajePagado.toFixed(0)}% pagado</Text>
          </View>

          <View style={styles.fechasGrid}>
            <View style={styles.fechaItem}>
              <View style={styles.fechaIconRow}>
                <Ionicons name="calendar-outline" size={15} color="#0066cc" />
                <Text style={styles.fechaLabel}>Emisión</Text>
              </View>
              <Text style={styles.fechaValue}>{cuenta.fecha_emision}</Text>
            </View>
            <View style={styles.fechaSep} />
            <View style={styles.fechaItem}>
              <View style={styles.fechaIconRow}>
                <Ionicons name="time-outline" size={15} color={cuenta.dias_restantes < 0 ? '#dc3545' : '#e67e22'} />
                <Text style={[styles.fechaLabel, cuenta.dias_restantes < 0 && { color: '#dc3545' }]}>Vencimiento</Text>
              </View>
              <Text style={[styles.fechaValue, cuenta.dias_restantes < 0 && { color: '#dc3545' }]}>{cuenta.fecha_vencimiento}</Text>
            </View>
            <View style={styles.fechaSep} />
            <View style={styles.fechaItem}>
              <View style={styles.fechaIconRow}>
                <Ionicons name="hourglass-outline" size={15} color="#6c757d" />
                <Text style={styles.fechaLabel}>Plazo</Text>
              </View>
              <View style={styles.plazoBadge}>
                <Text style={styles.plazoText}>{cuenta.dias_credito}d</Text>
              </View>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Botón de pago */}
      {cuenta.estado !== 'pagada' && (
        <Button
          mode="contained"
          icon="cash"
          onPress={() => { setMontoPago(String(cuenta.saldo_pendiente)); setShowPagoModal(true); }}
          style={styles.pagoButton}
          labelStyle={{ fontSize: 16 }}
        >
          Registrar Pago / Abono
        </Button>
      )}

      {/* Plan de cuotas */}
      {cuotas.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Plan de Pagos ({cuotas.length} {cuotas.length === 1 ? 'cuota' : 'cuotas'})</Text>
            {cuotas.map((c) => {
              const saldoCuota = parseFloat(c.saldo_cuota || 0);
              const pagada = c.estado === 'pagada';
              const vencida = !pagada && Number(c.dias_restantes) < 0;
              const porVencer = !pagada && !vencida && Number(c.dias_restantes) <= 3;
              const colorBorde = pagada ? '#28a745' : vencida ? '#dc3545' : porVencer ? '#ffc107' : '#6c757d';
              const iconName = pagada ? 'checkmark-circle' : vencida ? 'alert-circle' : 'time-outline';
              return (
                <View key={c.id} style={[styles.cuotaRow, { borderLeftColor: colorBorde }]}>
                  <View style={styles.cuotaIconCol}>
                    <Ionicons name={iconName} size={22} color={colorBorde} />
                    <Text style={styles.cuotaNum}>#{c.numero}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.cuotaFecha}>
                      Vence {new Date(c.fecha_vencimiento).toLocaleDateString('es-HN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                    <Text style={styles.cuotaEstado}>
                      {pagada ? 'Pagada' :
                       vencida ? `Vencida ${Math.abs(c.dias_restantes)}d` :
                       c.estado === 'parcial' ? `Parcial · saldo ${formatCurrency(saldoCuota)}` :
                       porVencer ? `Por vencer en ${c.dias_restantes}d` :
                       `En ${c.dias_restantes}d`}
                    </Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={[styles.cuotaMonto, pagada && { textDecorationLine: 'line-through', color: '#888' }]}>
                      {formatCurrency(c.monto)}
                    </Text>
                    {parseFloat(c.monto_pagado) > 0 && !pagada && (
                      <Text style={styles.cuotaPagado}>Abonado {formatCurrency(c.monto_pagado)}</Text>
                    )}
                  </View>
                </View>
              );
            })}
          </Card.Content>
        </Card>
      )}

      {/* Productos de la venta */}
      {detallesVenta.length > 0 && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.sectionTitle}>Productos (Venta #{cuenta.venta_id})</Text>
            {detallesVenta.map((d, i) => (
              <View key={i} style={styles.productoRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.productoNombre}>{d.producto_nombre}</Text>
                  <Text style={styles.subtext}>{d.cantidad} x {formatCurrency(d.precio_unitario)}</Text>
                </View>
                <Text style={styles.productoSubtotal}>{formatCurrency(d.subtotal)}</Text>
              </View>
            ))}
          </Card.Content>
        </Card>
      )}

      {/* Historial de pagos */}
      <Card style={styles.card}>
        <Card.Content>
          <Text style={styles.sectionTitle}>Historial de Pagos ({pagos.length})</Text>
          {pagos.length > 0 ? pagos.map((p, i) => (
            <View key={i} style={styles.pagoRow}>
              <View style={{ flex: 1 }}>
                <Text style={styles.pagoMonto}>{formatCurrency(p.monto)}</Text>
                <Text style={styles.subtext}>
                  {new Date(p.fecha_pago).toLocaleDateString('es-HN')} - {new Date(p.fecha_pago).toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit' })}
                </Text>
                <Text style={styles.subtext}>{p.metodo_pago} {p.recibido_por_nombre ? `- ${p.recibido_por_nombre}` : ''}</Text>
                {p.notas ? <Text style={[styles.subtext, { fontStyle: 'italic' }]}>{p.notas}</Text> : null}
              </View>
              <Ionicons name="checkmark-circle" size={20} color="#28a745" />
            </View>
          )) : (
            <Text style={styles.emptyText}>No hay pagos registrados</Text>
          )}
        </Card.Content>
      </Card>

      <View style={{ height: 30 }} />

      {/* Modal de pago */}
      <Portal>
        <Modal visible={showPagoModal} onDismiss={() => setShowPagoModal(false)} contentContainerStyle={styles.modal}>
          <Card>
            <Card.Title title="Registrar Pago" subtitle={`Saldo: ${formatCurrency(cuenta.saldo_pendiente)}`} />
            <Card.Content>
              <TextInput
                label="Monto del Pago (L.)"
                value={montoPago}
                onChangeText={setMontoPago}
                keyboardType="decimal-pad"
                style={styles.input}
              />
              <Text style={{ fontWeight: '600', marginTop: 8, marginBottom: 4 }}>Método de Pago</Text>
              <RadioButton.Group onValueChange={setMetodoPago} value={metodoPago}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                  {['efectivo', 'transferencia', 'tarjeta'].map(m => (
                    <View key={m} style={{ flexDirection: 'row', alignItems: 'center', marginRight: 12 }}>
                      <RadioButton value={m} />
                      <Text style={{ textTransform: 'capitalize' }}>{m}</Text>
                    </View>
                  ))}
                </View>
              </RadioButton.Group>
              <TextInput
                label="Notas (opcional)"
                value={notasPago}
                onChangeText={setNotasPago}
                style={styles.input}
                multiline
              />
              <View style={{ flexDirection: 'row', marginTop: 8 }}>
                {[0.25, 0.5, 1].map(pct => (
                  <Button
                    key={pct}
                    mode="outlined"
                    compact
                    onPress={() => setMontoPago(String((parseFloat(cuenta.saldo_pendiente) * pct).toFixed(2)))}
                    style={{ flex: 1, marginHorizontal: 2 }}
                  >
                    {pct === 1 ? 'Total' : `${pct * 100}%`}
                  </Button>
                ))}
              </View>
            </Card.Content>
            <Card.Actions style={{ justifyContent: 'space-between', padding: 8 }}>
              <Button onPress={() => setShowPagoModal(false)}>Cancelar</Button>
              <Button mode="contained" onPress={handleRegistrarPago} loading={pagoLoading} disabled={pagoLoading}>
                Confirmar Pago
              </Button>
            </Card.Actions>
          </Card>
        </Modal>
      </Portal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  card: { margin: 12, marginBottom: 0, elevation: 2, borderRadius: 8 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  clienteNombre: { fontSize: 18, fontWeight: '700', color: '#333' },
  negocioNombre: { fontSize: 14, color: '#666', fontStyle: 'italic' },
  subtext: { fontSize: 13, color: '#888', marginTop: 2 },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, gap: 5 },
  estadoChipText: { fontSize: 12, fontWeight: '700' },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: '#333', marginBottom: 10 },
  finRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4 },
  finLabel: { fontSize: 14, color: '#666' },
  finValue: { fontSize: 16, fontWeight: '700', color: '#333' },
  progressContainer: { marginTop: 12 },
  progressBar: { height: 8, backgroundColor: '#e9ecef', borderRadius: 4 },
  progressFill: { height: 8, backgroundColor: '#28a745', borderRadius: 4 },
  progressText: { fontSize: 12, color: '#888', marginTop: 4, textAlign: 'right' },
  fechasGrid: { flexDirection: 'row', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  fechaItem: { flex: 1, alignItems: 'center' },
  fechaSep: { width: 1, height: 36, backgroundColor: '#e0e0e0' },
  fechaIconRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 4 },
  fechaLabel: { fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3 },
  fechaValue: { fontSize: 13, fontWeight: '700', color: '#333' },
  plazoBadge: { backgroundColor: '#e9ecef', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10 },
  plazoText: { fontSize: 13, fontWeight: '700', color: '#495057' },
  pagoButton: { margin: 12, paddingVertical: 4, backgroundColor: '#28a745' },
  productoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  productoNombre: { fontSize: 14, fontWeight: '600', color: '#333' },
  productoSubtotal: { fontSize: 14, fontWeight: '700', color: '#0066cc' },
  pagoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  pagoMonto: { fontSize: 15, fontWeight: '700', color: '#28a745' },
  emptyText: { textAlign: 'center', color: '#999', fontStyle: 'italic', paddingVertical: 20 },
  modal: { margin: 16 },
  input: { marginBottom: 8, backgroundColor: '#fff' },
  cuotaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingLeft: 10,
    borderLeftWidth: 3,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  cuotaIconCol: { alignItems: 'center', width: 50 },
  cuotaNum: { fontSize: 11, fontWeight: '700', color: '#555', marginTop: 2 },
  cuotaFecha: { fontSize: 14, fontWeight: '700', color: '#333' },
  cuotaEstado: { fontSize: 12, color: '#666', marginTop: 2 },
  cuotaMonto: { fontSize: 15, fontWeight: '700', color: '#0066cc' },
  cuotaPagado: { fontSize: 11, color: '#28a745', marginTop: 2 },
});

export default DetalleCuentaScreen;
